
import { useState, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { useHydrationSafe } from "@/hooks/useHydrationSafe";
import { Mail, Phone, MapPin, Facebook, Instagram } from "lucide-react";
import { XTwitterIcon, PinterestIcon } from "@/components/icons/SocialIcons";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { PrivacyPolicyModal } from "@/components/PrivacyPolicyModal";
import { CookiePolicyModal } from "@/components/CookiePolicyModal";
import { DisclaimerModal } from "@/components/DisclaimerModal";
import { TermsOfServiceModal } from "@/components/TermsOfServiceModal";
import { CondizioniDealerModal } from "@/components/CondizioniDealerModal";
import { useDealerUtils } from "@/hooks/dealers/useDealerUtils";
import { useCitiesStatsPrefetch } from "@/hooks/useCitiesStatsPrefetch";
import { getSSRStableYear } from "@/utils/ssrTime";

// Lazy load ContactFormModal per evitare caricamento reCAPTCHA (714KB) su ogni pagina
const ContactFormModal = lazy(() => 
  import("@/components/ContactFormModal").then(m => ({ default: m.ContactFormModal }))
);

export function BusinessFooter() {
  // HYDRATION FIX: Use stable SSR year to prevent mismatch between server and client
  const currentYear = getSSRStableYear();
  const { isHydrated } = useHydrationSafe();
  const [showContactModal, setShowContactModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showCookieModal, setShowCookieModal] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showCondizioniDealerModal, setShowCondizioniDealerModal] = useState(false);
  const { createCitySlug } = useDealerUtils();
  const { prefetchCitiesStats } = useCitiesStatsPrefetch();

// Città statiche — renderizzate immediatamente per evitare CLS nel footer
const POPULAR_CITIES = [
  "Roma", "Milano", "Napoli", "Torino",
  "Palermo", "Genova", "Bologna", "Firenze",
  "Bari", "Catania", "Venezia", "Verona"
];


  return (
    <footer className="bg-card border-t pb-safe">
      <div className="container mx-auto py-6 px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-foreground">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-gold via-gold-light to-gold-dark dark:from-gold-light dark:via-gold dark:to-gold-dark">
                Gildy
              </span>
            </h3>
            <p className="text-sm text-muted-foreground/80 max-w-xs">
              La piattaforma leader per il confronto dei prezzi e delle valutazioni dell'oro.
            </p>
            <div className="flex space-x-3">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 rounded-full hover:bg-muted/80 transition-colors"
                asChild
              >
                <a 
                  href="https://www.facebook.com/gildyapp" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="Segui Gildy su Facebook"
                >
                  <Facebook className="h-4 w-4" />
                </a>
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 rounded-full hover:bg-muted/80 transition-colors"
                asChild
              >
                <a 
                  href="https://www.instagram.com/gildy.app/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="Segui Gildy su Instagram"
                >
                  <Instagram className="h-4 w-4" />
                </a>
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 rounded-full hover:bg-muted/80 transition-colors"
                asChild
              >
                <a 
                  href="https://x.com/gildy_app" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="Segui Gildy su X"
                >
                  <XTwitterIcon className="h-4 w-4" />
                </a>
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 rounded-full hover:bg-muted/80 transition-colors"
                asChild
              >
                <a 
                  href="https://it.pinterest.com/gildyapp/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="Segui Gildy su Pinterest"
                >
                  <PinterestIcon className="h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
          
          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground mb-4">Link Rapidi</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center">
                  <span className="inline-block w-2 h-2 bg-gold/50 rounded-full mr-2"></span>
                  Home
                </Link>
              </li>
              <li>
                <Link to="/prezzo-oro-oggi" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center">
                  <span className="inline-block w-2 h-2 bg-gold/50 rounded-full mr-2"></span>
                  Prezzo Oro Oggi
                </Link>
              </li>
              <li>
                <Link to="/prezzo-argento-oggi" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center">
                  <span className="inline-block w-2 h-2 bg-gold/50 rounded-full mr-2"></span>
                  Prezzo Argento Oggi
                </Link>
              </li>
              <li>
                <Link to="/compro-oro" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center">
                  <span className="inline-block w-2 h-2 bg-gold/50 rounded-full mr-2"></span>
                  Compro Oro
                </Link>
              </li>
              <li>
                <Link to="/vault" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center">
                  <span className="inline-block w-2 h-2 bg-gold/50 rounded-full mr-2"></span>
                  Il mio Vault
                </Link>
              </li>
              <li>
                <Link to="/verifica-compro-oro-oam" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center">
                  <span className="inline-block w-2 h-2 bg-gold/50 rounded-full mr-2"></span>
                  Verifica OAM
                </Link>
              </li>
              <li>
                <Link to="/guida/vendere-oro-usato-in-sicurezza" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center">
                  <span className="inline-block w-2 h-2 bg-gold/50 rounded-full mr-2"></span>
                  Guida Vendere Oro
                </Link>
              </li>
              <li>
                <Link to="/blocca-prezzo-oro" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center">
                  <span className="inline-block w-2 h-2 bg-gold/50 rounded-full mr-2"></span>
                  Blocca Prezzo Oro
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center">
                  <span className="inline-block w-2 h-2 bg-gold/50 rounded-full mr-2"></span>
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/registra-attivita" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center">
                  <span className="inline-block w-2 h-2 bg-gold/50 rounded-full mr-2"></span>
                  Registra la tua Attività
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Aggiunta sezione Compro Oro per città */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground mb-4">Compro Oro per Città</h3>
            <div className="grid grid-cols-2 gap-1">
              {POPULAR_CITIES.map((city) => (
                <Link 
                  key={city}
                  to={`/compro-oro/${createCitySlug(city)}`}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center"
                >
                  <span className="inline-block w-1.5 h-1.5 bg-gold/70 rounded-full mr-2"></span>
                  {city}
                </Link>
              ))}
            </div>
            <div className="mt-3 pt-2 border-t border-muted-foreground/10 space-y-2">
              <Link 
                to="/compro-oro-roma" 
                className="text-sm text-gold hover:text-gold-light transition-colors flex items-center font-medium"
              >
                <span className="inline-block w-1.5 h-1.5 bg-gold rounded-full mr-2"></span>
                Compro Oro Roma - Guida Completa
              </Link>
              <Link
                to="/compro-oro-milano"
                className="text-sm text-gold hover:text-gold-light transition-colors flex items-center font-medium"
              >
                <span className="inline-block w-1.5 h-1.5 bg-gold rounded-full mr-2"></span>
                Compro Oro Milano - Guida Completa
              </Link>
              <Link 
                to="/compro-oro-citta" 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center"
                onMouseEnter={() => {
                  prefetchCitiesStats();
                }}
              >
                <span className="inline-block w-1.5 h-1.5 bg-gold/50 rounded-full mr-2"></span>
                Vedi tutte le città
              </Link>
              <Link 
                to="/compro-oro-vicino-a-me" 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center"
              >
                <span className="inline-block w-1.5 h-1.5 bg-gold/50 rounded-full mr-2"></span>
                Compro Oro Vicino a Me
              </Link>
            </div>
          </div>
          
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground mb-4">Contatti</h3>
            <div className="space-y-3">
              <div className="flex items-center text-sm text-muted-foreground group hover:text-foreground transition-colors">
                <div className="w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center mr-3 group-hover:bg-gold/10">
                  <Phone className="h-3.5 w-3.5" />
                </div>
                <span>+39 (0)6 62 288 504</span>
              </div>
              <div className="flex items-center text-sm text-muted-foreground group hover:text-foreground transition-colors">
                <div className="w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center mr-3 group-hover:bg-gold/10">
                  <Mail className="h-3.5 w-3.5" />
                </div>
                <span>info@gildy.it</span>
              </div>
              <div className="flex items-center text-sm text-muted-foreground group hover:text-foreground transition-colors">
                <div className="w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center mr-3 group-hover:bg-gold/10">
                  <MapPin className="h-3.5 w-3.5" />
                </div>
                <span>Via Padova, 13, 00162 Roma, Italia</span>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full mt-2 bg-card hover:bg-muted/30 border-muted-foreground/20"
              onClick={() => setShowContactModal(true)}
            >
              Richiedi Informazioni
            </Button>
          </div>
        </div>
        
        <Separator className="my-4 opacity-30" />
        
        <div className="flex flex-col md:flex-row justify-between items-center text-xs">
          <div className="mb-4 md:mb-0">
            <p className="text-muted-foreground/70 mb-1">
              &copy; {currentYear} Gildy. Tutti i diritti riservati.
            </p>
            <p className="text-muted-foreground/70">
              Gildy è un marchio registrato da Twibi s.r.l.<br />
              P.I. IT15924631003.
            </p>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-2 justify-center">
            <button 
              onClick={() => setShowPrivacyModal(true)}
              className="text-muted-foreground/70 hover:text-foreground transition-colors"
            >
              Privacy Policy
            </button>
            <button 
              onClick={() => setShowCookieModal(true)}
              className="text-muted-foreground/70 hover:text-foreground transition-colors"
            >
              Cookie Policy
            </button>
            <Link 
              to="/privacy-center"
              className="text-muted-foreground/70 hover:text-foreground transition-colors"
            >
              Privacy Center
            </Link>
            <button 
              onClick={() => setShowTermsModal(true)}
              className="text-muted-foreground/70 hover:text-foreground transition-colors"
            >
              Termini di Servizio
            </button>
            <button 
              onClick={() => setShowCondizioniDealerModal(true)}
              className="text-muted-foreground/70 hover:text-foreground transition-colors"
            >
              Condizioni Operatori
            </button>
            <button 
              onClick={() => setShowDisclaimerModal(true)}
              className="text-muted-foreground/70 hover:text-foreground transition-colors"
            >
              Disclaimer
            </button>
          </div>
        </div>
        
        <div className="text-right mt-2">
          <a 
            href='#' 
            className='iubenda-cs-preferences-link text-muted-foreground/70 hover:text-foreground transition-colors text-xs'
          >  
            Le tue preferenze relative alla privacy
          </a>
        </div>
        
        <div className="text-center mt-6 text-xs text-muted-foreground/60">
          Made with <span className="text-rose-500">♥</span> by <a href="https://www.molecole.com" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">molecole</a>
        </div>
      </div>
      
      {/* 🛡️ HYDRATION FIX: usa isHydrated invece di !isSSR() per evitare React #418/#423.
          SSR e primo render client partono entrambi con isHydrated=false → markup identico.
          Post-hydration → true → modal montata senza mismatch. */}
      {isHydrated && (
        <Suspense fallback={null}>
          <ContactFormModal 
            isOpen={showContactModal} 
            onClose={() => setShowContactModal(false)} 
          />
        </Suspense>
      )}
      
      <PrivacyPolicyModal 
        isOpen={showPrivacyModal} 
        onClose={() => setShowPrivacyModal(false)} 
      />
      
      <CookiePolicyModal 
        isOpen={showCookieModal} 
        onClose={() => setShowCookieModal(false)} 
      />
      
      <DisclaimerModal 
        isOpen={showDisclaimerModal} 
        onClose={() => setShowDisclaimerModal(false)} 
      />
      
      <TermsOfServiceModal 
        isOpen={showTermsModal} 
        onClose={() => setShowTermsModal(false)} 
      />
      
      <CondizioniDealerModal 
        isOpen={showCondizioniDealerModal} 
        onClose={() => setShowCondizioniDealerModal(false)} 
      />
    </footer>
  );
}
